import { useQuery } from "@tanstack/react-query";
import { useState, type CSSProperties } from "react";

import { Avatar } from "@m2s/design-system";

import { ProfileDrawer } from "@/components/ProfileDrawer";
import { api } from "@/lib/api";
import type { Member } from "@/lib/types";
import { useAuthStore } from "@/store/auth";

const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  employee: "Employee",
  // Legacy values left for backward compatibility with any stale data.
  owner: "Admin",
  project_lead: "Admin",
  member: "Employee",
  viewer: "Employee",
};

const ADMIN_ROLES = new Set(["admin"]);

export function MembersPage() {
  const memberships = useAuthStore((s) => s.memberships);
  const isAdmin = memberships.some((m) => ADMIN_ROLES.has(m.role));
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const canViewProfiles = isAdmin || isSuperAdmin;

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string>("");

  // Backend already enforces with require(actor, "member.list") -> 403.
  // Gate the query on the same check so non-admins don't fire a doomed
  // network call (and don't accidentally hit a 403 toast in DevTools).
  // useQuery is called unconditionally to satisfy the rules of hooks;
  // `enabled` is what suppresses the fetch.
  const canList = isAdmin || isSuperAdmin;
  const { data: members = [], isLoading } = useQuery({
    queryKey: ["members"],
    queryFn: async () => (await api.get<Member[]>("/admin/members")).data,
    enabled: canList,
  });

  if (!canList) {
    return (
      <div style={{ maxWidth: 520 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", marginBottom: 12 }}>
          Members
        </h1>
        <p style={{ color: "var(--mute)", fontSize: 14, lineHeight: 1.6 }}>
          Member directory is visible to workspace admins only. You'll still
          see colleagues' names on issues, wiki pages, and approval queues
          you're part of.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", marginBottom: 24 }}>
        Members
      </h1>
      {isLoading ? (
        <p style={{ color: "var(--mute)" }}>Loading…</p>
      ) : (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: "var(--font-sans)",
          }}
        >
          <thead>
            <tr>
              {["Member", "Email", "Role", "Profile", "Status", ""].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: "left",
                    padding: "10px 8px",
                    borderBottom: "1px solid var(--hairline)",
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                    color: "var(--mute)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.user_id}>
                <td style={cellStyle}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                    <Avatar initials={initialsOf(m.display_name)} title={m.display_name} />
                    <span style={{ fontSize: 13 }}>{m.display_name}</span>
                  </span>
                </td>
                <td style={{ ...cellStyle, color: "var(--body-mid)", fontSize: 13 }}>
                  {m.email}
                </td>
                <td style={cellStyle}>
                  <span
                    style={{
                      fontSize: 11.5,
                      padding: "2px 8px",
                      borderRadius: 2,
                      background: "var(--surface-2)",
                      color: "var(--body-mid)",
                    }}
                  >
                    {ROLE_LABELS[m.role] ?? m.role}
                  </span>
                </td>
                <td style={cellStyle}>
                  <span style={profilePill(m.profile_complete)}>
                    {m.profile_complete ? "Complete" : "Pending"}
                  </span>
                </td>
                <td style={cellStyle}>
                  <span style={{ fontSize: 12.8, fontWeight: 550, color: "#047a18" }}>
                    {m.status}
                  </span>
                </td>
                <td style={{ ...cellStyle, textAlign: "right" }}>
                  {canViewProfiles && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedUserId(m.user_id);
                        setSelectedName(m.display_name);
                      }}
                      style={viewBtn}
                    >
                      View profile
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selectedUserId && (
        <ProfileDrawer
          userId={selectedUserId}
          displayName={selectedName}
          mode={isSuperAdmin && !isAdmin ? "superadmin" : "admin"}
          onClose={() => setSelectedUserId(null)}
        />
      )}
    </div>
  );
}

const cellStyle: CSSProperties = {
  padding: "10px 8px",
  borderBottom: "1px solid var(--hairline)",
};

const viewBtn: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "4px 10px",
  fontSize: 12,
  cursor: "pointer",
  color: "var(--ink)",
};

function profilePill(complete: boolean): CSSProperties {
  return {
    fontSize: 11,
    padding: "2px 8px",
    borderRadius: 2,
    background: complete ? "rgba(0,215,34,0.12)" : "rgba(255,174,19,0.14)",
    color: complete ? "#047a18" : "#8a5b00",
  };
}

function initialsOf(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
